import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useRouteError, isRouteErrorResponse, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@/lib/api';
import { Button, Card } from '@/components/ui';

/**
 * SHL-7 and SHL-8: an error boundary per route plus a global one, and a 404 that
 * links to a real route. The legacy 404 pointed at `../../index.html`.
 */

export class GlobalErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null }
> {
  override state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // NFR-8: log with enough context to correlate against the BFF's request ids.
    console.error('Unhandled UI error', { error, componentStack: info.componentStack });
  }

  override render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="grid min-h-screen place-items-center p-6">
        <Card className="max-w-md text-center">
          <h1 className="text-base font-semibold">Something went wrong</h1>
          <p className="mt-2 text-sm text-muted">
            The page hit an unexpected error. Reloading usually clears it.
          </p>
          <pre className="mt-3 max-h-32 overflow-auto rounded bg-raised p-2 text-start text-xs text-muted">
            {this.state.error.message}
          </pre>
          <div className="mt-4 flex justify-center gap-2">
            <Button onClick={() => this.setState({ error: null })}>Try again</Button>
            <Button variant="primary" onClick={() => window.location.assign('/')}>
              Go to dashboard
            </Button>
          </div>
        </Card>
      </div>
    );
  }
}

/**
 * Boundary for a lazily-loaded, non-essential widget.
 *
 * A chart chunk that fails to arrive -- a dev-server re-optimize, a deploy that
 * rotated the hashed filenames under an open tab, a flaky network -- rejects the
 * dynamic import, and without a boundary here that rejection climbs all the way
 * to the route boundary and replaces the entire dashboard with an error card.
 * The KPI row, funnel, revenue split and lawyer table have nothing to do with
 * Recharts and must survive it, so the failure is contained to its own tile.
 */
export class WidgetErrorBoundary extends Component<
  { children: ReactNode; fallback: ReactNode },
  { failed: boolean }
> {
  override state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Widget failed to render', { error, componentStack: info.componentStack });
  }

  override render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

/** Route-level boundary: one broken page never blanks the shell. */
export function RouteErrorBoundary() {
  const error = useRouteError();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const is404 = isRouteErrorResponse(error) && error.status === 404;

  const message = is404
    ? t('errors.pageNotFoundBody')
    : error instanceof ApiError
      ? error.message
      : error instanceof Error
        ? error.message
        : t('errors.generic');

  return (
    <Card className="mx-auto max-w-lg text-center">
      <h1 className="text-base font-semibold">
        {is404 ? t('errors.pageNotFoundTitle') : t('common.errorTitle')}
      </h1>
      <p className="mt-2 text-sm text-muted">{message}</p>
      {error instanceof ApiError && error.requestId && (
        <p className="mt-1 font-mono text-xs text-muted">
          {t('errors.requestId', { id: error.requestId })}
        </p>
      )}
      <div className="mt-4 flex justify-center gap-2">
        <Button onClick={() => navigate(-1)}>{t('common.back')}</Button>
        <Link
          to="/"
          className="inline-flex h-9 items-center rounded-md bg-brand px-3.5 text-sm font-medium text-brand-fg"
        >
          {t('errors.goHome')}
        </Link>
      </div>
    </Card>
  );
}

/** SHL-7: the 404 page proper. */
export function NotFoundPage() {
  const { t } = useTranslation();
  return (
    <div className="grid place-items-center py-20">
      <Card className="max-w-md text-center">
        <p className="text-4xl font-bold text-muted">404</p>
        <h1 className="mt-2 text-base font-semibold">{t('errors.pageNotFoundTitle')}</h1>
        <p className="mt-1 text-sm text-muted">{t('errors.pageNotFoundBody')}</p>
        <Link
          to="/"
          className="mt-4 inline-flex h-9 items-center rounded-md bg-brand px-3.5 text-sm font-medium text-brand-fg"
        >
          {t('errors.goHome')}
        </Link>
      </Card>
    </div>
  );
}
